import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  getTrail,
  editTrail,
  deleteTrail,
  listTrailAdmins,
  grantTrailAdmin,
  revokeTrailAdmin,
  listOrganizationsForCurator,
  createOrganization,
  ApiError,
  type TrailDetail,
  type TrailEditInput,
  type TrailAdmin,
  type OrgListRow,
} from "../api";
import { MapKitPlacePicker } from "./MapKitPlacePicker";
import { AmenitiesEditor } from "./AmenitiesEditor";
import { ActivityChipPicker } from "./ActivityChipPicker";
import { BannerUploader } from "./BannerUploader";
import type { ActivityKey } from "../activities";
import type { MapKitHit } from "../mapkit";

/**
 * Right-slide drawer (desktop) / bottom sheet (mobile) for editing a
 * single trail in the curator catalog. Replaces the disjoint Edit +
 * Re-locate modals from CatalogManager — both flows now live as
 * sections inside this one drawer so a curator stays in the same
 * mental context throughout.
 *
 * Sections (Phase 1):
 *   - The Basics  → name, region, state, country, website + status URL,
 *                   description
 *   - Drop a Pin  → lat/lng + embedded MapKit picker
 *
 * Phase 2+ will add Crew (admins), The Line (status override), and
 * Amenities. Status currently shows read-only context at the top.
 */

interface DraftState {
  name: string;
  region: string;
  state: string;
  country: string;
  statusPageUrl: string;
  websiteUrl: string;
  description: string;
  latitude: number | null;
  longitude: number | null;
  /** Parent organization id, or null when the trail isn't linked to
   *  one. Editing this field re-renders the trail's iOS/Android org
   *  chip on next fetch. */
  organizationId: string | null;
  /** Sorted comma-string snapshot of the activity tags so the diff
   *  helper can detect changes with a single string compare instead
   *  of array-equality juggling. The render layer keeps a Set for
   *  toggle ergonomics — these stay in sync via setDraft. */
  activitiesKey: string;
  activities: ActivityKey[];
}

function trailToDraft(t: TrailDetail): DraftState {
  const acts = ((t.activities ?? []) as ActivityKey[])
    .slice()
    .sort();
  return {
    name: t.name,
    region: t.region ?? "",
    state: t.state ?? "",
    country: t.country,
    statusPageUrl: t.statusPageUrl,
    websiteUrl: t.websiteUrl ?? "",
    description: t.description ?? "",
    latitude: t.latitude,
    longitude: t.longitude,
    organizationId: t.organization?.id ?? null,
    activities: acts,
    activitiesKey: acts.join(","),
  };
}

function draftDiff(original: DraftState, draft: DraftState): TrailEditInput {
  const out: TrailEditInput = {};
  if (draft.name.trim() !== original.name) out.name = draft.name.trim();
  if (draft.region !== original.region)
    out.region = draft.region.trim() || null;
  if (draft.state !== original.state) out.state = draft.state.trim() || null;
  if (draft.country !== original.country)
    out.country = draft.country.trim() || null;
  if (draft.statusPageUrl.trim() !== original.statusPageUrl)
    out.statusPageUrl = draft.statusPageUrl.trim();
  if (draft.websiteUrl !== original.websiteUrl)
    out.websiteUrl = draft.websiteUrl.trim() || null;
  if (draft.description !== original.description)
    out.description = draft.description.trim() || null;
  if (draft.latitude !== original.latitude) out.latitude = draft.latitude;
  if (draft.longitude !== original.longitude) out.longitude = draft.longitude;
  if (draft.organizationId !== original.organizationId)
    out.organizationId = draft.organizationId;
  if (draft.activitiesKey !== original.activitiesKey)
    out.activities = draft.activities;
  return out;
}

export function TrailDrawer({
  slug,
  onClose,
  onSaved,
  onDeleted,
}: {
  slug: string;
  onClose: () => void;
  /** Fired after a successful save so the parent list can refresh
   *  the row in place. Receives the updated TrailDetail so the
   *  parent can mirror without a refetch. */
  onSaved: (updated: TrailDetail) => void;
  /** Fired after a successful delete so the parent list can drop
   *  the row in place. The drawer animates closed itself. */
  onDeleted: (slug: string) => void;
}) {
  const [trail, setTrail] = useState<TrailDetail | null>(null);
  const [original, setOriginal] = useState<DraftState | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Crew (trail admins) state. Loaded lazily so a slow admin fetch
  // can't block the main trail edit form. Stays null until the first
  // fetch resolves — that way "Loading…" only shows once.
  const [crew, setCrew] = useState<TrailAdmin[] | null>(null);
  const [crewError, setCrewError] = useState<string | null>(null);
  const [crewEmail, setCrewEmail] = useState("");
  const [grantingCrew, setGrantingCrew] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Delete validator state. Opens an in-drawer confirmation panel
  // that requires the curator to type the trail name to enable the
  // Delete button (GitHub-style typed confirmation). Closing or
  // navigating away clears it. Browser confirm() would feel jarring
  // mid-drawer; this stays in the same surface and reinforces what's
  // being destroyed.
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Organizations list for the parent-org picker. Loaded once when
  // the drawer opens; cheap query (single SELECT against organizations).
  const [orgs, setOrgs] = useState<OrgListRow[] | null>(null);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  // Inline "create new org" flow — toggled from the picker. Keeps the
  // curator inside the trail drawer; no need to navigate to the
  // separate Curator panel just to stand up a real-world org like
  // "SORBA Woodstock".
  const [creatingOrgName, setCreatingOrgName] = useState<string | null>(null);
  const [creatingOrgBusy, setCreatingOrgBusy] = useState(false);
  const [createOrgError, setCreateOrgError] = useState<string | null>(null);

  // Scroll-to-top ref. The drawer stays mounted across slug changes
  // (we just swap the prop), so without this the body would inherit
  // the previous trail's scroll position — a "huh, where am I?"
  // moment for the curator.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Trigger the slide-in animation by mounting `closed` first, then
  // flipping to `open` on the next paint. Without this the transform
  // wouldn't animate — the element starts at its target position.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Swipe-to-close (mobile only). Touching the grabber/header and
  // dragging down past ~30% of drawer height triggers a close; under
  // that, the sheet snaps back. Only engages on viewports where the
  // drawer renders as a bottom sheet (≤720px) — desktop right-slide
  // ignores the gesture entirely.
  const drawerRef = useRef<HTMLElement | null>(null);
  const dragStateRef = useRef<{ startY: number; height: number } | null>(null);
  const [dragY, setDragY] = useState<number | null>(null);

  function isBottomSheet() {
    return typeof window !== "undefined" && window.innerWidth <= 720;
  }

  function onSheetTouchStart(e: React.TouchEvent) {
    if (!isBottomSheet() || !drawerRef.current) return;
    const touch = e.touches[0];
    dragStateRef.current = {
      startY: touch.clientY,
      height: drawerRef.current.getBoundingClientRect().height,
    };
    setDragY(0);
  }

  function onSheetTouchMove(e: React.TouchEvent) {
    const state = dragStateRef.current;
    if (!state) return;
    const delta = e.touches[0].clientY - state.startY;
    // Only follow downward drags; ignore upward (no over-stretch).
    setDragY(Math.max(0, delta));
  }

  function onSheetTouchEnd() {
    const state = dragStateRef.current;
    if (!state) {
      setDragY(null);
      return;
    }
    const finalDelta = dragY ?? 0;
    dragStateRef.current = null;
    if (finalDelta > state.height * 0.3) {
      // Past the threshold — close. Skip the dirty-check confirm so
      // a deliberate swipe doesn't get nagged.
      setOpen(false);
      setTimeout(onClose, 200);
    } else {
      // Snap back via the CSS transform transition.
      setDragY(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    // Reset transient UI state when the slug changes so a previously
    // opened delete prompt / saved-flash / scroll position doesn't
    // bleed into the next trail.
    setDeletePromptOpen(false);
    setDeleteConfirmText("");
    setDeleteError(null);
    setSaveError(null);
    setSavedAt(null);
    getTrail(slug)
      .then(({ trail }) => {
        if (cancelled) return;
        setTrail(trail);
        const seed = trailToDraft(trail);
        setOriginal(seed);
        setDraft(seed);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError ? err.message : "Couldn't load this trail."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Crew fetch — runs in parallel with the main trail load. We don't
  // gate the form on this; the section just shows its own spinner
  // while in flight.
  useEffect(() => {
    let cancelled = false;
    setCrew(null);
    setCrewError(null);
    listTrailAdmins(slug)
      .then((r) => {
        if (!cancelled) setCrew(r.items);
      })
      .catch((err) => {
        if (cancelled) return;
        setCrewError(
          err instanceof ApiError ? err.message : "Couldn't load the crew."
        );
        setCrew([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Load the org picker list once when the drawer opens. Decoupled
  // from the per-slug fetches above since the org catalog is global.
  useEffect(() => {
    let cancelled = false;
    listOrganizationsForCurator()
      .then((r) => {
        if (!cancelled) setOrgs(r.organizations);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrgsError(
          err instanceof ApiError ? err.message : "Couldn't load organizations."
        );
        setOrgs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleGrantCrew() {
    const email = crewEmail.trim();
    if (!email) return;
    setGrantingCrew(true);
    setCrewError(null);
    try {
      await grantTrailAdmin(slug, email);
      // Refetch so the new row carries the worker-generated metadata
      // (userId, grantedAt, etc.) without us guessing.
      const r = await listTrailAdmins(slug);
      setCrew(r.items);
      setCrewEmail("");
    } catch (err) {
      setCrewError(
        err instanceof ApiError ? err.message : "Couldn't grant access."
      );
    } finally {
      setGrantingCrew(false);
    }
  }

  async function handleDelete() {
    if (!trail) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteTrail(trail.slug);
      setOpen(false);
      // Wait for the slide-out transition before unmounting + telling
      // the parent so the row disappears at the same moment the drawer
      // does.
      setTimeout(() => onDeleted(trail.slug), 200);
    } catch (err) {
      setDeleteError(
        err instanceof ApiError ? err.message : "Couldn't delete the trail."
      );
      setDeleting(false);
    }
  }

  async function handleRevokeCrew(userId: string, email: string) {
    if (!confirm(`Revoke ${email}'s access to this trail?`)) return;
    setRevokingId(userId);
    setCrewError(null);
    try {
      await revokeTrailAdmin(slug, userId);
      setCrew((cur) => (cur ? cur.filter((m) => m.userId !== userId) : cur));
    } catch (err) {
      setCrewError(
        err instanceof ApiError ? err.message : "Couldn't revoke access."
      );
    } finally {
      setRevokingId(null);
    }
  }

  // Scroll the drawer body to the top on every slug change AND every
  // time the loaded trail switches identity. useLayoutEffect runs
  // synchronously after React commits the new DOM but BEFORE the
  // browser paints — so the curator never sees a flash of the
  // previous trail's scroll position before the reset.
  useLayoutEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [slug, trail?.slug]);

  // Esc key closes the drawer — matches Jakob's Law expectation for
  // modal-ish surfaces.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => {
    if (!original || !draft) return false;
    return Object.keys(draftDiff(original, draft)).length > 0;
  }, [original, draft]);

  function requestClose() {
    if (
      dirty &&
      !confirm("You have unsaved changes. Bail out and lose them?")
    ) {
      return;
    }
    setOpen(false);
    // Wait for the slide-out transition before unmounting.
    setTimeout(onClose, 200);
  }

  async function handleSave() {
    if (!trail || !original || !draft) return;
    const patch = draftDiff(original, draft);
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { trail: updated } = await editTrail(trail.slug, patch);
      setTrail(updated);
      const seed = trailToDraft(updated);
      setOriginal(seed);
      setDraft(seed);
      setSavedAt(Date.now());
      onSaved(updated);
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.message : "Couldn't save. Try again?"
      );
    } finally {
      setSaving(false);
    }
  }

  function applyPickedLocation(hit: MapKitHit) {
    setDraft((d) =>
      d
        ? {
            ...d,
            latitude: hit.latitude,
            longitude: hit.longitude,
            // Only backfill empty city/state fields — never clobber a
            // value a curator already set by hand. Apple's reverse
            // geocode is usually right, but trail names are noisy.
            region: d.region.trim() ? d.region : hit.city ?? d.region,
            state: d.state.trim() ? d.state : hit.stateCode ?? d.state,
          }
        : d
    );
  }

  return (
    <div
      className={`drawer-backdrop ${open ? "open" : ""}`}
      onClick={requestClose}
      aria-hidden={!open}
    >
      <aside
        ref={drawerRef}
        className={`drawer ${open ? "open" : ""} ${dragY != null ? "dragging" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={trail ? `Edit ${trail.name}` : "Loading trail"}
        style={
          dragY != null
            ? { transform: `translateY(${dragY}px)` }
            : undefined
        }
      >
        <header
          className="drawer-head"
          onTouchStart={onSheetTouchStart}
          onTouchMove={onSheetTouchMove}
          onTouchEnd={onSheetTouchEnd}
        >
          <div>
            <div className="drawer-eyebrow">Trail</div>
            <h2 className="drawer-title">{trail?.name ?? "Loading…"}</h2>
            {trail && (
              <div className="drawer-sub">
                {[trail.region, trail.state, trail.country]
                  .filter(Boolean)
                  .join(" · ") || "No location yet"}
              </div>
            )}
          </div>
          <button
            type="button"
            className="secondary small"
            onClick={requestClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="drawer-body" ref={bodyRef}>
          {loadError && (
            <div className="error" style={{ marginBottom: 12 }}>
              {loadError}
            </div>
          )}

          {draft && trail && (
            <>
              <Section
                title="The basics"
                hint="Who, where, and where it lives on the web."
              >
                <Field label="Trail name">
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) =>
                      setDraft({ ...draft, name: e.target.value })
                    }
                  />
                </Field>

                <div className="drawer-row">
                  <Field label="City / region">
                    <input
                      type="text"
                      value={draft.region}
                      placeholder="e.g. Woodstock"
                      onChange={(e) =>
                        setDraft({ ...draft, region: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="State">
                    <input
                      type="text"
                      value={draft.state}
                      placeholder="e.g. GA"
                      maxLength={4}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          state: e.target.value.toUpperCase(),
                        })
                      }
                    />
                  </Field>
                  <Field label="Country">
                    <input
                      type="text"
                      value={draft.country}
                      placeholder="US"
                      maxLength={2}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          country: e.target.value.toUpperCase(),
                        })
                      }
                    />
                  </Field>
                </div>

                <Field
                  label="Status page URL"
                  hint="Where the worker fetches the open/closed signal."
                >
                  <input
                    type="url"
                    value={draft.statusPageUrl}
                    onChange={(e) =>
                      setDraft({ ...draft, statusPageUrl: e.target.value })
                    }
                  />
                </Field>

                <Field label="Website (optional)">
                  <input
                    type="url"
                    value={draft.websiteUrl}
                    placeholder="https://"
                    onChange={(e) =>
                      setDraft({ ...draft, websiteUrl: e.target.value })
                    }
                  />
                </Field>

                <Field label="Description (optional)">
                  <textarea
                    rows={3}
                    value={draft.description}
                    placeholder="A short blurb riders will see."
                    onChange={(e) =>
                      setDraft({ ...draft, description: e.target.value })
                    }
                  />
                </Field>
              </Section>

              <Section
                title="Header image"
                hint="Facebook-style banner that lands at the top of the trail's detail screen on iOS + Android. Curator-grade — same upload path admins use, no claim required."
              >
                <BannerUploader slug={trail.slug} />
              </Section>

              <Section
                title="Activities"
                hint="Which disciplines belong on this trail? Multi-select — pick every activity the trail system supports. Drives the Discover filter on iOS + Android."
              >
                <ActivityChipPicker
                  selected={new Set<ActivityKey>(draft.activities)}
                  onToggle={(key) => {
                    const next = new Set<ActivityKey>(draft.activities);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    const sorted = [...next].sort() as ActivityKey[];
                    setDraft({
                      ...draft,
                      activities: sorted,
                      activitiesKey: sorted.join(","),
                    });
                  }}
                />
              </Section>

              <Section
                title="Drop a pin"
                hint="Search Apple Maps, or drag the pin to where the trailhead actually is."
              >
                <div className="drawer-coords">
                  <span className="muted">Current pin</span>
                  <code>
                    {draft.latitude != null && draft.longitude != null
                      ? `${draft.latitude.toFixed(5)}, ${draft.longitude.toFixed(5)}`
                      : "Not set yet"}
                  </code>
                </div>
                <MapKitPlacePicker
                  embedded
                  initialQuery={draft.name}
                  nearCity={draft.region}
                  nearState={draft.state}
                  initialLatitude={draft.latitude}
                  initialLongitude={draft.longitude}
                  onPick={applyPickedLocation}
                />
              </Section>

              <Section
                title="Organization"
                hint="The volunteer group that stewards this trail (e.g. SORBA Woodstock). Shown on the iOS + Android detail screen with their logo. One org can own many trails."
              >
                {orgsError && (
                  <div className="error" style={{ marginBottom: 8 }}>
                    {orgsError}
                  </div>
                )}
                {orgs === null ? (
                  <div className="muted">Loading organizations…</div>
                ) : creatingOrgName !== null ? (
                  <Field
                    label="Name the new organization"
                    hint='Use the real-world name — e.g. "SORBA Woodstock", "Atlanta MTB Coalition".'
                  >
                    <input
                      type="text"
                      autoFocus
                      placeholder="SORBA Woodstock"
                      value={creatingOrgName}
                      onChange={(e) => setCreatingOrgName(e.target.value)}
                      disabled={creatingOrgBusy}
                    />
                    {createOrgError && (
                      <div className="error" style={{ marginTop: 8 }}>
                        {createOrgError}
                      </div>
                    )}
                    <div className="drawer-section-actions" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="primary"
                        disabled={
                          creatingOrgBusy ||
                          (creatingOrgName ?? "").trim().length === 0
                        }
                        onClick={async () => {
                          const name = (creatingOrgName ?? "").trim();
                          if (!name) return;
                          setCreatingOrgBusy(true);
                          setCreateOrgError(null);
                          try {
                            const { organization } = await createOrganization(name);
                            // Wrap the create response (EmbeddedOrganization)
                            // into the OrgListRow shape the list uses. A new
                            // org always starts with zero linked trails until
                            // the save below attaches this one.
                            const asListRow: OrgListRow = {
                              ...organization,
                              trailCount: 0,
                            };
                            setOrgs((prev) =>
                              (prev ?? [])
                                .concat([asListRow])
                                .sort((a, b) =>
                                  a.name.localeCompare(b.name)
                                )
                            );
                            // Auto-select the freshly created org so the
                            // curator's intent ("make + assign") carries
                            // through with one tap.
                            setDraft((d) =>
                              d ? { ...d, organizationId: organization.id } : d
                            );
                            setCreatingOrgName(null);
                          } catch (err) {
                            setCreateOrgError(
                              err instanceof ApiError
                                ? err.message
                                : "Couldn't create the organization."
                            );
                          } finally {
                            setCreatingOrgBusy(false);
                          }
                        }}
                      >
                        {creatingOrgBusy ? "Creating…" : "Create + assign"}
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        disabled={creatingOrgBusy}
                        onClick={() => {
                          setCreatingOrgName(null);
                          setCreateOrgError(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </Field>
                ) : (
                  <Field
                    label="Linked organization"
                    hint="Pick an existing org or create one. Many trails can share one org (e.g. SORBA Woodstock stewards Blankets Creek + Rope Mill)."
                  >
                    <select
                      value={draft.organizationId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "__create__") {
                          setCreatingOrgName("");
                          setCreateOrgError(null);
                          return;
                        }
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                organizationId: value === "" ? null : value,
                              }
                            : d
                        );
                      }}
                    >
                      <option value="">— No organization —</option>
                      <option value="__create__">+ Create new organization…</option>
                      {orgs.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </Section>

              <Section
                title="Crew"
                hint="People who can set status overrides for this trail."
              >
                {crewError && (
                  <div className="error" style={{ marginBottom: 8 }}>
                    {crewError}
                  </div>
                )}
                {crew === null ? (
                  <div className="muted" style={{ fontSize: 13 }}>
                    Loading the crew…
                  </div>
                ) : crew.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>
                    No one's assigned yet. Add a trail admin by email below.
                  </div>
                ) : (
                  <ul className="crew-list" role="list">
                    {crew.map((m) => (
                      <li key={m.userId} className="crew-row">
                        <div className="crew-row-main">
                          <div className="crew-row-email">{m.email}</div>
                          <div className="crew-row-meta muted">
                            {m.role}
                            {m.grantedAt
                              ? ` · granted ${new Date(m.grantedAt * 1000).toLocaleDateString()}`
                              : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="small secondary"
                          disabled={revokingId === m.userId}
                          onClick={() => handleRevokeCrew(m.userId, m.email)}
                          aria-label={`Revoke ${m.email}`}
                        >
                          {revokingId === m.userId ? "…" : "Revoke"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="crew-grant">
                  <input
                    type="email"
                    placeholder="rider@example.com"
                    value={crewEmail}
                    onChange={(e) => setCrewEmail(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={handleGrantCrew}
                    disabled={grantingCrew || !crewEmail.trim()}
                  >
                    {grantingCrew ? "Adding…" : "Add to crew"}
                  </button>
                </div>
              </Section>

              <Section
                title="Amenities"
                hint="What's at the trailhead. Riders see these on the detail screen. As a curator you can edit any trail's amenities — you don't need to be on the crew."
              >
                <AmenitiesEditor slug={trail.slug} />
              </Section>
            </>
          )}
        </div>

        <footer className="drawer-foot">
          <div className="drawer-foot-actions">
            <button
              type="button"
              className="small trail-delete-btn"
              onClick={() => setDeletePromptOpen(true)}
              disabled={!trail || saving || deleting}
              title="Permanently remove this trail from the catalog"
              aria-label={
                trail
                  ? `Delete ${trail.name} from the catalog`
                  : "Delete trail"
              }
            >
              <span aria-hidden="true" className="trail-delete-icon">🗑</span>
              <span>Delete trail</span>
            </button>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="secondary"
                onClick={requestClose}
                disabled={saving}
              >
                Bail out
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <div className="drawer-foot-status">
            {saving ? (
              <span className="muted">Saving…</span>
            ) : saveError ? (
              <span className="error">{saveError}</span>
            ) : savedAt ? (
              <span className="muted">Saved · {timeAgo(savedAt)}</span>
            ) : dirty ? (
              <span className="muted">Unsaved changes</span>
            ) : (
              <span className="muted">No changes yet</span>
            )}
          </div>
        </footer>

        {deletePromptOpen && trail && (
          <DeleteValidator
            trailName={trail.name}
            confirmText={deleteConfirmText}
            onChange={setDeleteConfirmText}
            onCancel={() => {
              setDeletePromptOpen(false);
              setDeleteConfirmText("");
              setDeleteError(null);
            }}
            onConfirm={handleDelete}
            busy={deleting}
            error={deleteError}
          />
        )}
      </aside>
    </div>
  );
}

function DeleteValidator({
  trailName,
  confirmText,
  onChange,
  onCancel,
  onConfirm,
  busy,
  error,
}: {
  trailName: string;
  confirmText: string;
  onChange: (s: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
  error: string | null;
}) {
  // Match is case-insensitive but otherwise exact — small forgiveness
  // for typo-prone inputs without weakening the safety check.
  const matches = confirmText.trim().toLowerCase() === trailName.trim().toLowerCase();
  return (
    <div className="delete-validator" role="alertdialog" aria-label="Confirm delete">
      <div className="delete-validator-card">
        <div className="delete-validator-eyebrow">Heads up</div>
        <h3>Delete this trail?</h3>
        <p className="muted">
          This drops <strong>{trailName}</strong> from the catalog, every
          rider's saved list, and every push subscription. It can't be
          undone.
        </p>
        <label className="drawer-field" style={{ marginTop: 12 }}>
          <span className="drawer-field-label">
            Type the trail name to confirm
          </span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => onChange(e.target.value)}
            placeholder={trailName}
            autoFocus
          />
        </label>
        {error && (
          <div className="error" style={{ fontSize: 12, marginTop: 8 }}>
            {error}
          </div>
        )}
        <div
          className="row"
          style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}
        >
          <button
            type="button"
            className="secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="danger"
            onClick={onConfirm}
            disabled={!matches || busy}
          >
            {busy ? "Deleting…" : "Delete trail"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="drawer-section">
      <header>
        <h3>{title}</h3>
        {hint && <p className="muted">{hint}</p>}
      </header>
      <div className="drawer-section-body">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="drawer-field">
      <span className="drawer-field-label">{label}</span>
      {children}
      {hint && <span className="drawer-field-hint">{hint}</span>}
    </label>
  );
}

function timeAgo(ts: number): string {
  const delta = Math.max(0, Date.now() - ts);
  const seconds = Math.floor(delta / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
